// React
import React, { useMemo } from 'react';

// Vendors
import { DesignSystem } from '@equisoft/design-elements-react';
import { createBrowserRouter, RouterProvider, RouteObject } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Src
import PluginsLoader from '@/infrastructure/pluginsFramework/PluginsLoader';
import themes from '@/ui/themes';
import UnprotectedRoute from '@/application/router/UnprotectedRoute';
import ProtectedRoute from '@/application/router/ProtectedRoute';
import CanvasLayout from '@/application/layouts/CanvasLayout/CanvasLayout';
import DefaultLayout from '@/application/layouts/DefaultLayout/DefaultLayout';
import LoginPage from '@/application/pages/Login/Login';
import ForgotPasswordPage from '@/application/pages/ForgotPassword/ForgotPassword';
import HomePage from '@/application/pages/Home/Home';
import NotFoundPage from '@/application/pages/NotFound/NotFound';
import SnackBar from '@/application/components/SnackBar/SnackBar';
import { useAuthentication } from '@/domain/authentication/hooks/useAuthentication';
import { usePluginsConfig } from './pluginsConfig';

const useRouter = () => {
    const pluginsConfig = usePluginsConfig();

    const routeObjects: RouteObject[] = [];

    for (const plugin of pluginsConfig) {
        for (const route of plugin.anchorConfig.routeConfig) {
            routeObjects.push({
                path: route.path,
                element: (
                    <PluginsLoader
                        key={route.element}
                        config={{
                            scope: route.remoteConfig?.scope || 'default',
                            url: route.remoteConfig?.url || '',
                            module: route.element,
                        }}
                        context={{
                            anchorNames: 'admin_console',
                            domain: route.domain,
                        }}
                    />
                ),
            });
        }
    }

    const router = [
        {
            element: <UnprotectedRoute />,
            children: [
                {
                    element: <CanvasLayout />,
                    children: [
                        { path: '/login', element: <LoginPage /> },
                        { path: '/forgot-password', element: <ForgotPasswordPage /> },
                    ],
                },
            ],
        },
        {
            element: <ProtectedRoute />,
            children: [
                {
                    path: '/',
                    element: <DefaultLayout />,
                    children: [
                        ...routeObjects,
                        {
                            index: true,
                            element: <HomePage />,
                        },
                        {
                            path: '*',
                            element: <NotFoundPage />,
                        },
                    ],
                },
            ],
        },
    ];

    return createBrowserRouter(router);
};

const App = () => {
    // Hooks
    const router = useRouter();
    const { i18n } = useTranslation();
    const { isReady } = useAuthentication();
    const routerMemo = useMemo(() => router, []);

    // Variables
    const currentTheme = themes.default;
    const designSystemProps = { theme: currentTheme.designElements };

    if (!isReady) return null;

    return (
        <React.Fragment key={i18n.language}>
            <DesignSystem {...designSystemProps}>
                <RouterProvider router={routerMemo} />
                <SnackBar />
            </DesignSystem>
        </React.Fragment>
    );
};

export default App;
